/**
 * Full-screen tournament standings from the tournament-standings API (signed-in only).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, Platform, ActivityIndicator } from 'react-native';
import { Swords, Table2, X, type LucideIcon } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BaseModal } from '../../ui/BaseModal/BaseModal';
import { useTheme } from '../../../src/theme/useTheme';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import {
  WORLD_CUP_COMPETITION_ID,
  WORLD_CUP_STANDINGS_MODAL,
} from '../../../src/worldcup/worldcup.constants';
import { useWorldCupTournamentStandingsQuery } from '../../../src/worldcup/hooks/useWorldCupTournamentStandingsQuery';
import { getWorldCupStandingsModalStyles } from './WorldCupStandingsModal.styles';
import { WorldCupGroupStageTableCard } from './WorldCupGroupStageTableCard';
import { KnockoutView } from '../KnockoutView/KnockoutView';
import {
  getStandingsEmptySubtitle,
  STANDINGS_MODAL_COPY,
  STANDINGS_VIEWS,
  WORLD_CUP_STANDINGS_MODAL_TEST_ID,
  type StandingsModalView,
} from './worldCupStandingsModal.constants';

export interface WorldCupStandingsModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  /** Which tab to open on. Defaults to the group-stage table. */
  initialView?: StandingsModalView;
  testID?: string;
}

const TABS: readonly { readonly id: StandingsModalView; readonly label: string; readonly Icon: LucideIcon }[] =
  [
    { id: STANDINGS_VIEWS.GROUPS, label: STANDINGS_MODAL_COPY.GROUPS_TAB, Icon: Table2 },
    { id: STANDINGS_VIEWS.KNOCKOUT, label: STANDINGS_MODAL_COPY.KNOCKOUT_TAB, Icon: Swords },
  ];

export const WorldCupStandingsModal: React.FC<WorldCupStandingsModalProps> = ({
  isVisible,
  onDismiss,
  initialView = STANDINGS_VIEWS.GROUPS,
  testID,
}) => {
  const theme = useTheme();
  const { isSignedIn, isLoaded } = useSupabaseAuth();
  const insets = useSafeAreaInsets();
  const styles = useMemo(
    () => getWorldCupStandingsModalStyles(theme, { safeAreaTop: insets.top }),
    [theme, insets.top],
  );
  const [view, setView] = useState<StandingsModalView>(initialView);
  const showWebHover = Platform.OS === 'web';
  const modalTestId = testID ?? WORLD_CUP_STANDINGS_MODAL_TEST_ID;
  const canFetchStandings = isVisible && isLoaded && isSignedIn;
  const standingsQuery = useWorldCupTournamentStandingsQuery(
    WORLD_CUP_COMPETITION_ID,
    canFetchStandings,
  );

  const groupStageTables = isSignedIn ? (standingsQuery.data?.groups ?? []) : [];
  const showLoading = isSignedIn && (standingsQuery.isLoading || standingsQuery.isFetching) && !standingsQuery.data;
  const showError = isSignedIn && standingsQuery.isError;
  const showEmpty = !showLoading && !showError && groupStageTables.length === 0;

  useEffect(() => {
    if (isVisible) setView(initialView);
  }, [isVisible, initialView]);

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={onDismiss}
      dismissible={false}
      blurIntensity={WORLD_CUP_STANDINGS_MODAL.BASE_MODAL_BLUR_INTENSITY}
      contentAlignment="flex-start"
      contentPosition={{ top: 0, right: 0, bottom: 0, left: 0 }}
      contentContainerStyle={styles.fullScreen}
      testID={modalTestId}
      contentTestID={testID ? `${testID}-content` : `${WORLD_CUP_STANDINGS_MODAL_TEST_ID}-content`}
    >
      <View style={styles.fullScreen}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>{STANDINGS_MODAL_COPY.TITLE}</Text>
            <Pressable
              style={({ pressed, hovered }) => [
                styles.closeButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.closeButtonPressed,
                hovered && showWebHover && styles.closeButtonHover,
              ]}
              onPress={onDismiss}
              accessibilityRole="button"
              accessibilityLabel={STANDINGS_MODAL_COPY.ACCESSIBILITY_CLOSE}
              hitSlop={12}
              testID={testID ? `${testID}-close` : `${WORLD_CUP_STANDINGS_MODAL_TEST_ID}-close`}
            >
              <X size={20} color={theme.gray[600]} strokeWidth={2.2} />
            </Pressable>
          </View>

          <View style={styles.tabBar}>
            <View style={styles.tabRow}>
              {TABS.map(({ id, label, Icon }) => {
                const active = view === id;
                return (
                  <Pressable
                    key={id}
                    style={({ pressed, hovered }) => [
                      styles.tabButton,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.tabButtonPressed,
                      hovered && showWebHover && !active && styles.tabButtonHoverInactive,
                    ]}
                    onPress={() => setView(id)}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: active }}
                    testID={testID ? `${testID}-tab-${id}` : `${WORLD_CUP_STANDINGS_MODAL_TEST_ID}-tab-${id}`}
                  >
                    <Icon size={16} color={active ? theme.primary : theme.gray[500]} strokeWidth={2.2} />
                    <Text
                      style={[
                        styles.tabLabel,
                        active ? styles.tabLabelActive : styles.tabLabelInactive,
                      ]}
                    >
                      {label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
            <View style={styles.tabUnderlineTrack}>
              <View
                style={[
                  styles.tabUnderline,
                  view === STANDINGS_VIEWS.KNOCKOUT && styles.tabUnderlineRight,
                ]}
              />
            </View>
          </View>
        </View>

        {view === STANDINGS_VIEWS.GROUPS ? (
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={Platform.OS !== 'web'}
          >
            <Text style={styles.groupsSectionTitle}>{STANDINGS_MODAL_COPY.SECTION_GROUP_STANDINGS}</Text>
            {showLoading ? (
              <View style={styles.stateWrap}>
                <ActivityIndicator size="small" color={theme.primary} />
                <Text style={styles.stateTitle}>{STANDINGS_MODAL_COPY.LOADING}</Text>
              </View>
            ) : null}
            {showError ? (
              <View style={styles.stateWrap}>
                <Text style={styles.stateTitle}>{STANDINGS_MODAL_COPY.ERROR_TITLE}</Text>
                <Text style={styles.stateSubtitle}>{STANDINGS_MODAL_COPY.ERROR_SUBTITLE}</Text>
              </View>
            ) : null}
            {showEmpty ? (
              <View style={styles.stateWrap}>
                <Text style={styles.stateTitle}>{STANDINGS_MODAL_COPY.EMPTY_TITLE}</Text>
                <Text style={styles.stateSubtitle}>{getStandingsEmptySubtitle()}</Text>
              </View>
            ) : null}
            {!showLoading && !showError && !showEmpty
              ? groupStageTables.map((t) => (
                <WorldCupGroupStageTableCard
                  key={t.isThirdPlaceRanking ? 'third-place-ranking' : t.groupLetter}
                  groupLetter={t.groupLetter}
                  teams={t.teams}
                  hasResults={t.hasResults ?? false}
                  isThirdPlaceRanking={t.isThirdPlaceRanking ?? false}
                />
              ))
              : null}
            <Text style={styles.footnote}>
              {isSignedIn ? STANDINGS_MODAL_COPY.FOOTNOTE : STANDINGS_MODAL_COPY.GUEST_FOOTNOTE}
            </Text>
          </ScrollView>
        ) : (
          // Live knockout bracket — or a provisional preview while the draw is pending.
          <KnockoutView />
        )}
      </View>
    </BaseModal>
  );
};
